import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Ban, RotateCcw, PowerOff, Trash2, Mail, Save, AlertTriangle } from "lucide-react";
import * as A from "@/lib/accountsApi";
import { formatApiError } from "@/lib/auth";
import { AREAS, ROLE_LABEL, STATUS_LABEL, STATUS_TONE } from "@/lib/access";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmAction } from "@/components/contacts/ConfirmAction";
import { AccountRecords } from "@/components/contacts/AccountRecords";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong");

function ContactEdit({ a, onSaved }) {
  const [name, setName] = useState(a.name || "");
  const [phone, setPhone] = useState(a.phone || "");
  const dirty = name !== (a.name || "") || phone !== (a.phone || "");
  const save = () => A.editAccountContact(a.user_id, { name, phone }).then((r) => { onSaved(r); toast.success("Contact details saved"); }).catch(err);
  return (
    <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
      <label className="text-xs text-stone-600">Name<Input data-testid="acct-name-input" value={name} onChange={(e) => setName(e.target.value)} disabled={!a.can.edit} className="mt-1" /></label>
      <label className="text-xs text-stone-600">Phone<Input data-testid="acct-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={!a.can.edit} className="mt-1" /></label>
      {a.can.edit && <Button data-testid="acct-contact-save" onClick={save} disabled={!dirty} variant="outline" className="h-10"><Save className="h-4 w-4 mr-1" />Save</Button>}
    </div>
  );
}

function RoleEditor({ a, onSaved }) {
  const [role, setRole] = useState(a.role);
  const [perms, setPerms] = useState(a.permissions);
  const [confirm, setConfirm] = useState(false);
  const dirty = role !== a.role || (role === "staff" && [...perms].sort().join() !== [...a.permissions].sort().join());
  const toggle = (k) => setPerms((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5" role="radiogroup">
        {Object.entries(ROLE_LABEL).map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={role === k} data-testid={`acct-role-${k}`} onClick={() => setRole(k)}
            className={`h-10 rounded-lg border text-xs font-semibold ${role === k ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-300 text-stone-700"}`}>{l}</button>
        ))}
      </div>
      {role === "staff" && (
        <div data-testid="acct-staff-areas" className="rounded-lg border border-stone-200 p-2.5 grid sm:grid-cols-2 gap-1.5">
          <p className="sm:col-span-2 text-xs text-stone-500">Staff can only open the areas ticked here.</p>
          {AREAS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid={`acct-area-${k}`} checked={perms.includes(k)} onChange={() => toggle(k)} className="h-4 w-4" />{l}</label>
          ))}
        </div>
      )}
      <Button data-testid="acct-role-save" disabled={!dirty} onClick={() => setConfirm(true)} className="bg-stone-900 text-white h-10"><ShieldCheck className="h-4 w-4 mr-1" />Save role</Button>
      <ConfirmAction open={confirm} onClose={() => setConfirm(false)} testId="confirm-role" confirmLabel="Change role"
        title={`Change role to ${ROLE_LABEL[role]}?`} danger={role === "super_admin"}
        body={<><p>{a.email} is currently <strong>{ROLE_LABEL[a.role]}</strong>.</p>
          {role === "super_admin" && <p className="text-red-700">Super Admins have full access, including managing every account and deleting users.</p>}
          {role === "seller" && a.role !== "seller" && <p>Their administrative access ends on their next action.</p>}</>}
        onConfirm={({ reason }) => A.changeRole(a.user_id, { role, permissions: perms, reason }).then((r) => { onSaved(r); toast.success("Role updated"); }).catch((e) => { err(e); throw e; })} />
    </div>
  );
}

const ACTIONS = [
  ["suspend", "Suspend", Ban, "Suspend this account?", "They're signed out everywhere immediately and can't sign in. Their horses, applications, contracts and history are kept.", (a, r) => A.changeStatus(a.user_id, { status: "suspended", reason: r }), true],
  ["reactivate", "Reactivate", RotateCcw, "Reactivate this account?", "They'll be able to sign in again with a new sign-in link.", (a, r) => A.changeStatus(a.user_id, { status: "active", reason: r }), false],
  ["deactivate", "Deactivate", PowerOff, "Deactivate this account?", "Login is disabled and all sessions end. Nothing is deleted, and a Super Admin can reactivate it later.", (a, r) => A.changeStatus(a.user_id, { status: "deactivated", reason: r }), true],
  ["delete", "Delete", Trash2, "Move to Deleted Contacts?", "Login access is removed, sessions end, and the contact is hidden from active contacts. All information, horses, contracts, applications and messages are preserved, and a Super Admin can restore it.", (a, r) => A.softDelete(a.user_id, { reason: r }), true],
  ["restore", "Restore", RotateCcw, "Restore this contact?", "The account becomes active again and returns to the main contacts list.", (a, r) => A.restoreAccount(a.user_id, { reason: r }), false],
];

function StatusActions({ a, onSaved, onPurged }) {
  const [open, setOpen] = useState(null);
  const [plan, setPlan] = useState(null);
  const act = ACTIONS.find((x) => x[0] === open);
  const openPurge = () => A.purgePreview(a.user_id).then((p) => { setPlan(p); setOpen("purge"); }).catch(err);
  const resend = () => A.resendLink(a.user_id).then(() => toast.success("Sign-in link sent")).catch(err);
  return (
    <div className="flex flex-wrap gap-2">
      {a.can.resend_link && <Button data-testid="acct-resend-link" variant="outline" onClick={resend} className="h-10"><Mail className="h-4 w-4 mr-1" />Resend sign-in link</Button>}
      {ACTIONS.filter(([k]) => a.can[k]).map(([k, l, Icon, , , , danger]) => (
        <Button key={k} data-testid={`acct-${k}`} variant="outline" onClick={() => setOpen(k)} className={`h-10 ${danger ? "text-red-700 border-red-200 hover:bg-red-50" : ""}`}><Icon className="h-4 w-4 mr-1" />{l}</Button>
      ))}
      {a.can.purge && <Button data-testid="acct-purge" onClick={openPurge} className="h-10 bg-red-700 hover:bg-red-800 text-white"><AlertTriangle className="h-4 w-4 mr-1" />Permanently delete</Button>}
      {act && (
        <ConfirmAction open onClose={() => setOpen(null)} testId={`confirm-${act[0]}`} title={act[3]} body={<p>{act[4]}</p>} confirmLabel={act[1]} danger={act[6]}
          onConfirm={({ reason }) => act[5](a, reason).then((r) => { onSaved(r); toast.success(`${act[1]} done`); }).catch((e) => { err(e); throw e; })} />
      )}
      {open === "purge" && plan && (
        <ConfirmAction open onClose={() => setOpen(null)} testId="confirm-purge" title="Permanently delete personal information?" danger confirmLabel="Permanently delete" typeToConfirm={plan.email}
          body={<>
            <p className="font-semibold text-red-700">This can't be undone.</p>
            <p className="font-semibold mt-2">Will be permanently removed:</p>
            <ul data-testid="purge-removed" className="list-disc pl-5">{plan.removed.map((t) => <li key={t}>{t}</li>)}</ul>
            <p className="font-semibold mt-2">Will be kept (required business records):</p>
            <ul data-testid="purge-retained" className="list-disc pl-5">{plan.retained.map((t) => <li key={t}>{t}</li>)}</ul>
          </>}
          onConfirm={() => A.purgeAccount(a.user_id, plan.email).then(() => { toast.success("Personal information permanently deleted"); onPurged(); }).catch((e) => { err(e); throw e; })} />
      )}
    </div>
  );
}

export function AccountManager({ uid, onChanged }) {
  const [a, setA] = useState(null);
  useEffect(() => { A.getAccount(uid).then(setA).catch(() => setA({ error: true })); }, [uid]);
  const saved = (r) => { setA(r); onChanged?.(); };
  if (!a) return <p className="text-sm text-stone-500">Loading account…</p>;
  if (a.error) return <p className="text-sm text-red-700">Could not load this account.</p>;
  return (
    <div data-testid="account-manager" className="space-y-5 rounded-xl border border-stone-200 bg-stone-50/60 p-3 sm:p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid="acct-role" className="inline-flex h-6 items-center px-2 rounded-full border border-stone-300 bg-white text-[11px] font-semibold">{ROLE_LABEL[a.role]}</span>
        <span data-testid="acct-status" className={`inline-flex h-6 items-center px-2 rounded-full border text-[11px] font-semibold ${STATUS_TONE[a.status]}`}>{STATUS_LABEL[a.status]}</span>
        {a.status_reason && a.status !== "active" && <span className="text-xs text-stone-500">“{a.status_reason}”</span>}
      </div>
      <section><h4 className="text-sm font-semibold mb-1.5">Contact information</h4><ContactEdit key={`${a.name}|${a.phone}`} a={a} onSaved={saved} /></section>
      {a.can.change_role && <section><h4 className="text-sm font-semibold mb-1.5">Role and permissions</h4><RoleEditor key={`${a.role}|${a.permissions.join()}`} a={a} onSaved={saved} /></section>}
      {a.role === "staff" && !a.can.change_role && <p className="text-xs text-stone-600">Staff areas: {a.permissions.map((p) => AREAS.find(([k]) => k === p)?.[1]).join(", ") || "none"}</p>}
      <section><h4 className="text-sm font-semibold mb-1.5">Account access</h4><StatusActions a={a} onSaved={saved} onPurged={() => onChanged?.(true)} /></section>
      <section className="text-xs text-stone-600">
        Contact preference: <strong>{{ email: "Email", sms: "Text message", both: "Email and text" }[a.contact_prefs?.contact_preference] || "Not set"}</strong>
        {a.contact_prefs?.sms_consent ? " · text consent given" : ""}
      </section>
      <AccountRecords a={a} />
    </div>
  );
}
